import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';

import { DataTestidDirective } from '@@shared/data-test-id-directive';
import { ButtonComponent } from '@@shared/design/components/button/button.component';
import { TextButtonComponent } from '@@shared/design/components/text-button/text-button.component';

import { UploadDialogModel, UploadDialogTestIds } from '../../models/upload-dialog.model';
import { DialogComponent } from '../dialog/dialog.component';

@Component({
	selector: 'sl-upload-dialog',
	templateUrl: './upload-dialog.component.html',
	styleUrl: './upload-dialog.component.scss',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		DecimalPipe,
		DialogComponent,
		MatDialogClose,
		MatIcon,
		MatProgressBar,
		ButtonComponent,
		TextButtonComponent,
		DataTestidDirective,
	],
})
export class UploadDialogComponent implements OnInit {
	readonly error = signal<string>(null);
	readonly errorMessage = signal<string>(null);
	readonly isLoading = signal(false);
	readonly uploadFinished = signal(false);
	readonly uploadSuccessMessage = signal<string>(null);
	readonly config = signal<UploadDialogModel>(inject(MAT_DIALOG_DATA));
	readonly testIds = UploadDialogTestIds;
	readonly #destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.#setSubs();
	}

	onConfirm(): void {
		this.isLoading.set(true);
		this.config()?.onSave?.emit();
	}

	onCancel(): void {
		this.config()?.onCancel?.emit();
	}

	onView(): void {
		this.config()?.onAction?.emit('view');
	}

	#setSubs(): void {
		this.#subOnError();
		this.#subUploadSuccess();
	}

	#subOnError(): void {
		this.config()
			?.onError?.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(errorObj => {
				this.error.set(errorObj.title);
				this.errorMessage.set(errorObj.message);
				this.isLoading.set(false);
			});
	}

	#subUploadSuccess(): void {
		this.config()
			?.onUploadSuccess?.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(message => {
				this.uploadSuccessMessage.set(message);
				this.uploadFinished.set(true);
				this.isLoading.set(false);
			});
	}
}
